import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbHighlight, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Subject, catchError, filter, finalize, first, switchMap, takeUntil, tap } from 'rxjs';

import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { CoreModule } from 'src/app/core/core.module';
import {
  InventoryItemComponent as InventoryItemComponentModel,
  InventoryItemComponentCreate,
  InventoryItemComponentUpdate,
} from 'src/app/core/models/inventory/inventory-item-component.model';
import { InventoryItemComponentFormModel } from 'src/app/core/models/inventory/inventory-item-component-form.model';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import {
  InventoryItemSearchOption,
  inventoryItemOptionCompare,
  inventoryItemOptionFilter,
  inventoryItemOptionFormatter,
} from 'src/app/core/utils/inventory-item-search.util';

// Pre-computed view-model for a single component row. Every render-time value is
// resolved on the component-side (never via methods in the template) per AGENTS rules.
interface InventoryItemComponentRowView {
  id: string;
  componentInventoryItemId: string;
  componentTitle: string;
  componentSku: string;
  hasSku: boolean;
  baseUomCode: string;
  quantity: number;
  sortOrder: number;
  // Avatar visuals — precomputed so the template never recomputes per CD cycle.
  initials: string;
  avatarClass: string;
}

@Component({
  selector: 'app-inventory-item-components-tab',
  standalone: true,
  imports: [CommonModule, CoreModule, NgbHighlight, ReactiveFormsModule, TranslateModule],
  providers: [InventoryItemComponentFormModel],
  templateUrl: './inventory-item-components-tab.component.html',
  styleUrl: './inventory-item-components-tab.component.scss',
})
export class InventoryItemComponentsTabComponent implements OnChanges, OnDestroy {
  readonly componentFormModel = inject(InventoryItemComponentFormModel);
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly modalService = inject(NgbModal);
  private readonly translateService = inject(TranslateService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public itemId = '';
  @Input({ required: true }) public inventoryItemOptions: InventoryItemSearchOption[] = [];
  @Input() public isReadonly = false;

  @Output() public readonly componentsChanged = new EventEmitter<InventoryItemComponentModel[]>();

  public readonly componentsView$ = new BehaviorSubject<InventoryItemComponentRowView[]>([]);
  public readonly childOptions$ = new BehaviorSubject<InventoryItemSearchOption[]>([]);

  // Bound directly in the template to the <app-search> filter; calls the shared util
  // with `excludeComposite: true` so users cannot pick a composite item as a child.
  public readonly childFilterFn = (option: InventoryItemSearchOption, term: string): boolean =>
    inventoryItemOptionFilter(option, term, { excludeComposite: true });
  public readonly childFormatter = inventoryItemOptionFormatter;
  public readonly childCompareFn = inventoryItemOptionCompare;

  public isLoading = false;
  public isCreatingRow = false;
  public isSavingRow = false;
  public isDeletingComponentId = '';
  public editingComponentId = '';
  public rowErrorKey = '';
  public tabErrorKey = '';

  // Selected child preview cells (precomputed so the create-row template can bind
  // to plain fields instead of calling methods).
  public createChildSku = '—';
  public createChildBaseUom = '—';

  private rawComponents: InventoryItemComponentModel[] = [];
  private readonly unsubscribe$ = new Subject<void>();

  // Deterministic palette of 8 Tailwind light/dark pairs reused across both desktop
  // avatar circles and mobile-card avatars. Sourced from the Tailwind scales that
  // already map onto the project palette in `_color-palette.scss`; no raw HEX values.
  private readonly avatarPalette: readonly string[] = [
    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
    'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
    'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200',
    'bg-blue-200 text-blue-800 dark:bg-blue-800/40 dark:text-blue-100',
  ];

  // Neutral fallback when displayName is null/empty — paired with the fallback icon.
  private readonly avatarFallbackClass =
    'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500';

  constructor() {
    this.form.controls.componentInventoryItemId.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((value) => this.applySelectedChildPreview(value)),
      )
      .subscribe();
  }

  public get form() {
    return this.componentFormModel.form;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['inventoryItemOptions']) {
      this.refreshChildOptions();
      this.refreshComponentsView();
    }

    if (changes['companyId'] || changes['itemId']) {
      this.loadComponents();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public startCreateRow(): void {
    if (this.isReadonly || this.isCreatingRow || this.editingComponentId) {
      return;
    }
    this.rowErrorKey = '';
    this.tabErrorKey = '';
    this.resetSelectedChildPreview();
    this.form.reset({
      componentInventoryItemId: null,
      quantity: 1,
      sortOrder: this.suggestNextSortOrder(),
    });
    this.isCreatingRow = true;
  }

  public startEditRow(componentId: string): void {
    if (this.isReadonly || this.isCreatingRow || this.editingComponentId) {
      return;
    }
    const row = this.rawComponents.find((candidate) => candidate.id === componentId);
    if (!row) {
      return;
    }

    this.editingComponentId = componentId;
    this.rowErrorKey = '';
    this.tabErrorKey = '';
    this.resetSelectedChildPreview();
    this.form.reset({
      componentInventoryItemId: row.componentInventoryItemId,
      quantity: row.quantity,
      sortOrder: row.sortOrder,
    });
  }

  public cancelEditing(): void {
    if (this.isSavingRow) {
      return;
    }
    this.endRowEditing();
  }

  public saveRow(): void {
    if (!this.companyId || !this.itemId || this.isSavingRow || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.editingComponentId) {
      this.executeUpdate();
      return;
    }

    this.executeCreate();
  }

  public deleteRow(componentId: string): void {
    if (!this.companyId || !this.itemId || !componentId || this.isDeletingComponentId) {
      return;
    }

    const modalRef = this.modalService.open(ConfirmModalComponent, { size: 'sm' });
    modalRef.componentInstance.message = this.translateService.instant(
      'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.REMOVE_CONFIRM',
    );

    modalRef.closed
      .pipe(
        first(),
        filter((confirmed) => confirmed === true),
        tap(() => {
          this.isDeletingComponentId = componentId;
          this.tabErrorKey = '';
        }),
        switchMap(() => this.inventoryItemService.deleteComponent(this.companyId, this.itemId, componentId)),
        switchMap(() => this.inventoryItemService.getComponents(this.companyId, this.itemId)),
        takeUntil(this.unsubscribe$),
        tap((components) => this.applyComponents(components)),
        catchError(() => {
          this.tabErrorKey = 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.DELETE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isDeletingComponentId = '';
        }),
      )
      .subscribe();
  }

  private executeCreate(): void {
    const payload: InventoryItemComponentCreate = {
      componentInventoryItemId: this.form.controls.componentInventoryItemId.value ?? '',
      quantity: this.form.controls.quantity.value,
      sortOrder: this.form.controls.sortOrder.value ?? undefined,
    };

    this.isSavingRow = true;
    this.rowErrorKey = '';

    this.inventoryItemService
      .addComponent(this.companyId, this.itemId, payload)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => this.inventoryItemService.getComponents(this.companyId, this.itemId)),
        tap((components) => {
          this.applyComponents(components);
          this.endRowEditing();
        }),
        catchError((error) => this.handleRowError(error)),
        finalize(() => {
          this.isSavingRow = false;
        }),
      )
      .subscribe();
  }

  private executeUpdate(): void {
    const row = this.rawComponents.find((candidate) => candidate.id === this.editingComponentId);
    if (!row) {
      return;
    }

    const payload: InventoryItemComponentUpdate = {
      quantity: this.form.controls.quantity.value,
      sortOrder: this.form.controls.sortOrder.value ?? undefined,
      expectedConcurrencyToken: row.concurrencyToken,
    };

    this.isSavingRow = true;
    this.rowErrorKey = '';

    this.inventoryItemService
      .updateComponent(this.companyId, this.itemId, this.editingComponentId, payload)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => this.inventoryItemService.getComponents(this.companyId, this.itemId)),
        tap((components) => {
          this.applyComponents(components);
          this.endRowEditing();
        }),
        catchError((error) => this.handleRowError(error)),
        finalize(() => {
          this.isSavingRow = false;
        }),
      )
      .subscribe();
  }

  private loadComponents(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isLoading = true;
    this.tabErrorKey = '';

    this.inventoryItemService
      .getComponents(this.companyId, this.itemId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((components) => this.applyComponents(components)),
        catchError(() => {
          this.tabErrorKey = 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.LOAD_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private applyComponents(components: InventoryItemComponentModel[]): void {
    this.rawComponents = [...components].sort(
      (left, right) => left.sortOrder - right.sortOrder,
    );
    this.componentsView$.next(this.rawComponents.map((row) => this.mapRow(row)));
    this.componentsChanged.emit(this.rawComponents);
  }

  private mapRow(component: InventoryItemComponentModel): InventoryItemComponentRowView {
    const option = this.inventoryItemOptions.find(
      (candidate) => candidate.id === component.componentInventoryItemId,
    );
    const rawDisplayName = component.componentDisplayName?.trim() || option?.title || '';
    const fallbackTitle = rawDisplayName || component.componentNumber?.trim() || '—';
    const skuRaw = component.componentNumber?.trim() || option?.number || '';
    const initials = this.computeInitials(rawDisplayName);
    const avatarClass = initials
      ? this.avatarPalette[this.hashToPaletteIndex(rawDisplayName)]
      : this.avatarFallbackClass;

    return {
      id: component.id,
      componentInventoryItemId: component.componentInventoryItemId,
      componentTitle: fallbackTitle,
      componentSku: skuRaw,
      hasSku: skuRaw.length > 0,
      // Empty string (not em-dash) when missing — matches the convention used by
      // peer CRM tables (e.g. sales-order-lines): the UoM column simply renders an
      // empty cell when the row has no unit-of-measure code.
      baseUomCode: component.componentBaseUnitOfMeasureCode?.trim() ?? '',
      quantity: component.quantity,
      sortOrder: component.sortOrder,
      initials,
      avatarClass,
    };
  }

  // Up to 2 uppercase initials from the displayName.
  //  - "Wheel hub"     -> "WH"
  //  - "Bearing"       -> "BE"
  //  - "Шайба гайка"   -> "ШГ"  (locale-aware uppercase keeps Cyrillic correct)
  //  - "" / undefined  -> ""    (caller falls back to the neutral icon variant)
  private computeInitials(displayName: string | null | undefined): string {
    const trimmed = (displayName ?? '').trim();
    if (!trimmed) {
      return '';
    }
    const words = trimmed.split(/\s+/).filter((part) => part.length > 0);
    if (words.length === 0) {
      return '';
    }
    if (words.length === 1) {
      return words[0].slice(0, 2).toLocaleUpperCase();
    }
    return (words[0].charAt(0) + words[1].charAt(0)).toLocaleUpperCase();
  }

  // Deterministic hash so the same displayName always maps to the same palette pair.
  // Sum-of-char-codes is sufficient for color anchoring — no crypto required.
  private hashToPaletteIndex(displayName: string): number {
    let sum = 0;
    for (let index = 0; index < displayName.length; index += 1) {
      sum += displayName.charCodeAt(index);
    }
    return sum % this.avatarPalette.length;
  }

  private refreshComponentsView(): void {
    this.componentsView$.next(this.rawComponents.map((row) => this.mapRow(row)));
  }

  private refreshChildOptions(): void {
    // The shared filter handles `excludeComposite` for keyword searches, but the
    // dropdown options list itself should also exclude composites so the empty-state
    // (typeahead opened with no term) does not surface composite candidates.
    this.childOptions$.next(this.inventoryItemOptions.filter((option) => !option.isComposite));
  }

  private endRowEditing(): void {
    this.isCreatingRow = false;
    this.editingComponentId = '';
    this.rowErrorKey = '';
    this.resetSelectedChildPreview();
  }

  private applySelectedChildPreview(childId: string | null): void {
    if (!childId) {
      this.resetSelectedChildPreview();
      return;
    }
    const option = this.inventoryItemOptions.find((candidate) => candidate.id === childId);
    this.createChildSku = option?.number || '—';
    this.createChildBaseUom = option?.baseUomCode?.trim() || '—';
  }

  private resetSelectedChildPreview(): void {
    this.createChildSku = '—';
    this.createChildBaseUom = '—';
  }

  private suggestNextSortOrder(): number {
    if (this.rawComponents.length === 0) {
      return 1;
    }
    const max = Math.max(...this.rawComponents.map((row) => row.sortOrder));
    return Number.isFinite(max) ? max + 1 : this.rawComponents.length + 1;
  }

  private handleRowError(error: unknown) {
    if (error instanceof HttpErrorResponse && error.status === 409) {
      this.rowErrorKey = 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.DUPLICATE_COMPONENT';
    } else {
      this.rowErrorKey = 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.SAVE_FAILED';
    }
    return EMPTY;
  }
}
